import { SetBasketModel } from 'pmg-services';
import { services } from '../services';

const basketStore = {
  state: {
    basket: undefined,
    clubExtrasAvailable: false, // don't call this  without doing the dispatch checkIfClubExtrasAvailable
    donation: {
      bundleId: 0,
      clubId: 0,
      amount: 0,
    },
    lateReferral: {
      referrerIdentifier: '',
      isVerified: false,
      referrerName: '',
      referrerGuid: null,
    },
    selectedProfessional: null,
    selectedLessonBundle: null,
  },

  getters: {
    getBasket: state => state.basket,
    // isPMGPlus is a points-shape heuristic (0 home, 100+ flexi) that also matches
    // large flexi top-ups, so additionally require an Experience/PMGPlus package type.
    isPmgPlusBasket: state => !!(state.basket && state.basket.isPMGPlus
      && (state.basket.PackageType === 3 || state.basket.PackageType === 4)),
    // Experience and PMG Plus bundles (PackageType 3/4): the buyer earns no referral bonus.
    isExperienceTierBasket: state => !!(state.basket
      && (state.basket.PackageType === 3 || state.basket.PackageType === 4)),
  },

  mutations: {
    setCharityDonation(state, amount) {
      state.donation = {
        bundleId: state.basket.BundleId,
        clubId: state.basket.NewClubId,
        amount,
      };
    },

    emptyCharityDonation(state) {
      state.donation = {
        bundleId: 0,
        clubId: 0,
        amount: 0,
      };
    },

    pushBasket(state, basket) {
      // Auto-select mandatory extras if not already selected.
      // Exception: when a club has 2+ mandatory affiliation fee options, do not auto-select them
      // so the user can choose exactly one.
      let updatedBasket = basket;
      if (basket && Array.isArray(basket.AdditionalExtras)) {
        const isAffiliationFeeType = x => x && (x.Type === 0 || x.ExtraType === 0);

        const mandatoryAffiliationFees = basket.AdditionalExtras
          .filter(extra => extra && extra.IsMandatory && isAffiliationFeeType(extra));
        const hasMultipleMandatoryAffiliationFees = mandatoryAffiliationFees.length >= 2;

        // Defensive normalisation: some backends/older responses can mark multiple mandatory
        // affiliation fee options as Selected=true (because they're mandatory), even though the
        // basket hasn't actually included them yet. That breaks our radio UX + pricing.
        //
        // For multi-option MAFs, force 0-or-1 selected in UI state:
        // - Prefer the server's "selected extras" list if present
        // - Else, keep a single Selected flag if exactly one is set
        // - Else, clear all to force an explicit choice
        let selectedMandatoryAffiliationFeeId = null;
        if (hasMultipleMandatoryAffiliationFees) {
          const selectedFromExtrasList = Array.isArray(basket.Extras)
            ? basket.Extras
              .filter(x => x && isAffiliationFeeType(x))
              .map(x => x.Id)
            : [];

          if (selectedFromExtrasList.length === 1) {
            selectedMandatoryAffiliationFeeId = selectedFromExtrasList[0];
          } else {
            const selectedFromFlags = mandatoryAffiliationFees
              .filter(x => x && x.Selected === true)
              .map(x => x.Id);

            if (selectedFromFlags.length === 1) {
              selectedMandatoryAffiliationFeeId = selectedFromFlags[0];
            }
          }
        }

        updatedBasket = {
          ...basket,
          AdditionalExtras: basket.AdditionalExtras.map((extra) => {
            if (!extra) return extra;

            const isMandatoryAffiliationFeeOption = extra.IsMandatory && isAffiliationFeeType(extra);

            if (hasMultipleMandatoryAffiliationFees && isMandatoryAffiliationFeeOption) {
              const shouldSelect = selectedMandatoryAffiliationFeeId !== null
                && extra.Id === selectedMandatoryAffiliationFeeId;

              return extra.Selected === shouldSelect
                ? extra
                : { ...extra, Selected: shouldSelect };
            }

            if (extra.IsMandatory
              && !extra.Selected
              && !(hasMultipleMandatoryAffiliationFees && isMandatoryAffiliationFeeOption)
            ) {
              return { ...extra, Selected: true };
            }
            return extra;
          }),
        };
      }

      state.basket = updatedBasket;

      if (basket === null || basket.NewClubId !== state.donation.clubId || basket.BundleId !== state.donation.bundleId) {
        state.donation = {
          bundleId: 0,
          clubId: 0,
          amount: 0,
        };
      }
    },

    setClubExtrasAvailable(state, available) {
      state.clubExtrasAvailable = available;
    },

    setLateReferral(state, {
      referrerIdentifier, isVerified, referrerName, referrerGuid = null,
    }) {
      state.lateReferral = {
        referrerIdentifier,
        isVerified,
        referrerName,
        referrerGuid,
      };
    },

    clearLateReferral(state) {
      state.lateReferral = {
        referrerIdentifier: '',
        isVerified: false,
        referrerName: '',
        referrerGuid: null,
      };
    },

    setSelectedProfessional(state, professional) {
      state.selectedProfessional = professional;
    },

    setSelectedLessonBundle(state, lessonBundle) {
      state.selectedLessonBundle = lessonBundle;
    },
  },

  actions: {
    setCharityDonation(context, amount) {
      context.commit('setCharityDonation', amount);
    },

    emptyCharityDonation(context) {
      context.commit('emptyCharityDonation');
    },

    async setBasketBundle(context, bundle) {
      return new Promise((resolve) => {
        context.commit('setRefreshingBasket', true, { root: true });
        if (bundle.preserveCharity === undefined || bundle.preserveCharity !== true) {
          context.commit('emptyCharityDonation');
        }

        // Ensure mandatory extras are always included.
        // Exception: when there are 2+ mandatory affiliation fee options, do not auto-inject them.
        let updatedBundle = bundle;
        if (context.state.basket && Array.isArray(context.state.basket.AdditionalExtras)) {
          const allMandatoryExtras = context.state.basket.AdditionalExtras
            .filter(x => x && x.IsMandatory);

          const mandatoryAffiliationFees = allMandatoryExtras
            .filter(x => x.Type === 0);

          const hasMultipleMandatoryAffiliationFees = mandatoryAffiliationFees.length >= 2;

          const mandatoryExtraIds = allMandatoryExtras
            .filter(x => !hasMultipleMandatoryAffiliationFees || x.Type !== 0)
            .map(x => x.Id);

          const baseIds = Array.isArray(bundle.AdditionalExtrasIds)
            ? bundle.AdditionalExtrasIds.slice()
            : null;

          if (baseIds) {
            // Add mandatory extras if not already in the list
            mandatoryExtraIds.forEach((id) => {
              if (!baseIds.includes(id)) {
                baseIds.push(id);
              }
            });
            updatedBundle = { ...bundle, AdditionalExtrasIds: baseIds };
          } else {
            updatedBundle = { ...bundle, AdditionalExtrasIds: mandatoryExtraIds };
          }
        }

        const professionalId = context.state.selectedProfessional
          ? context.state.selectedProfessional.Id
          : null;

        services.checkout
          .setBasket(
            updatedBundle.BundleId,
            updatedBundle.PromoCode,
            updatedBundle.NewClubId,
            updatedBundle.AdditionalBundleIds,
            null,
            updatedBundle.AdditionalExtrasIds,
            professionalId,
          )
          .then(() => {
            services.checkout.getBasket(null, true).then((basket) => {
              context.commit('pushBasket', basket);
              context.commit('setRefreshingBasket', false, { root: true });

              resolve();
            });
          })
          .catch((error) => {
            context.commit('setRefreshingBasket', false, { root: true });
            console.error('Failed to update basket:', error);
            context.dispatch('refreshBasket');
            resolve();
          });
      });
    },

    async toggleExtraInBasket(context, payload) {
      if (!context.state.basket || !Array.isArray(context.state.basket.AdditionalExtras)) {
        return;
      }

      const extras = context.state.basket.AdditionalExtras;
      const extra = extras.find(x => x.Id === payload.id);
      if (!extra) return;

      const mandatoryAffiliationFees = extras
        .filter(x => x && x.IsMandatory && x.Type === 0);
      const hasMultipleMandatoryAffiliationFees = mandatoryAffiliationFees.length >= 2;
      const isMandatoryAffiliationFeeOption = extra.IsMandatory && extra.Type === 0;

      // Prevent toggling mandatory extras, except affiliation fee options when multiple exist.
      if (extra.IsMandatory && (!isMandatoryAffiliationFeeOption || !hasMultipleMandatoryAffiliationFees)) {
        return;
      }

      let selectedIds = extras
        .filter(x => x.Selected === true)
        .map(x => x.Id);

      if (isMandatoryAffiliationFeeOption && hasMultipleMandatoryAffiliationFees) {
        // Radio semantics: selecting one option clears any other mandatory affiliation fee selection.
        const mandatoryAffiliationFeeIds = mandatoryAffiliationFees.map(x => x.Id);
        const selectedMandatoryAffiliationFeeIds = mandatoryAffiliationFees
          .filter(x => x && x.Selected === true)
          .map(x => x.Id);

        // If we're in a bad state (2+ selected) and the user clicks one that's already selected,
        // treat that click as "choose this one" and clear the rest.
        if (selectedIds.includes(payload.id) && selectedMandatoryAffiliationFeeIds.length <= 1) {
          return;
        }

        selectedIds = selectedIds.filter(id => !mandatoryAffiliationFeeIds.includes(id));
        selectedIds.push(payload.id);

        if (payload.skipBackend) {
          extras.forEach((x) => {
            if (x && x.IsMandatory && x.Type === 0) {
              // eslint-disable-next-line no-param-reassign
              x.Selected = x.Id === payload.id;
            }
          });
        }
      } else if (selectedIds.includes(payload.id)) {
        selectedIds = selectedIds.filter(x => x !== payload.id);
        if (payload.skipBackend) {
          const target = extras.find(x => x.Id === payload.id);
          if (target) target.Selected = false;
        }
      } else {
        selectedIds.push(payload.id);
        if (payload.skipBackend) {
          const target = extras.find(x => x.Id === payload.id);
          if (target) target.Selected = true;
        }
      }


      const selectedAddons = [...context.state.basket.AdditionalItems.filter(x => x.Selected === true).map(x => x.Id)];
      if (context.state.selectedLessonBundle) {
        selectedAddons.push(context.state.selectedLessonBundle.BundleId);
      }
      const bundle = new SetBasketModel(context.state.basket.BundleId, context.state.basket.PromoCode, context.state.basket.NewClubId, null, selectedAddons, selectedIds);
      // bad
      bundle.preserveCharity = true;

      if (payload.skipBackend === undefined || !payload.skipBackend) { await context.dispatch('setBasketBundle', bundle); }
    },

    async toggleAddOnInBasket(context, id) {
      let selectedIds = [...context.state.basket.AdditionalItems.filter(x => x.Selected === true)
        .map(x => x.Id)];

      const exists = [...selectedIds.filter(x => x === id)];
      if (exists.length > 0) {
        selectedIds = [];
      } else {
        selectedIds = [id];
      }

      if (context.state.basket && context.state.basket.AdditionalItems) {
        context.state.basket.AdditionalItems.forEach((item) => {
          // eslint-disable-next-line no-param-reassign
          item.Selected = selectedIds.includes(item.Id);
        });
      }

      if (context.state.selectedLessonBundle) {
        selectedIds.push(context.state.selectedLessonBundle.BundleId);
      }

      const selectedExtras = [...context.state.basket.AdditionalExtras.filter(x => x.Selected === true).map(x => x.Id)];
      const bundle = new SetBasketModel(context.state.basket.BundleId, context.state.basket.PromoCode, context.state.basket.NewClubId, null, selectedIds, selectedExtras);
      // bad
      bundle.preserveCharity = true;

      await context.dispatch('setBasketBundle', bundle);
    },

    async togglePromoCodeInBasket(context, promoCode) {
      const selectedIds = [...context.state.basket.AdditionalItems.filter(x => x.Selected === true).map(x => x.Id)];
      if (context.state.selectedLessonBundle) {
        selectedIds.push(context.state.selectedLessonBundle.BundleId);
      }
      const selectedExtras = [...context.state.basket.AdditionalExtras.filter(x => x.Selected === true).map(x => x.Id)];

      const bundle = new SetBasketModel(context.state.basket.BundleId, promoCode, context.state.basket.NewClubId, null, selectedIds, selectedExtras);
      // bad
      bundle.preserveCharity = true;

      await context.dispatch('setBasketBundle', bundle);
    },

    async toggleLessonBundle(context, lessonPackage) {
      const currentBundle = context.state.selectedLessonBundle;
      const isSelected = currentBundle && currentBundle.BundleId === lessonPackage.BundleId;

      context.commit('setSelectedLessonBundle', isSelected ? null : lessonPackage);

      if (isSelected) {
        context.commit('setSelectedProfessional', null);
      }

      let selectedAddOns = [...context.state.basket.AdditionalItems.filter(x => x.Selected === true).map(x => x.Id)];
      if (isSelected) {
        selectedAddOns = selectedAddOns.filter(id => id !== lessonPackage.BundleId);
      } else if (!selectedAddOns.includes(lessonPackage.BundleId)) {
        selectedAddOns.push(lessonPackage.BundleId);
      }

      const selectedExtras = [...context.state.basket.AdditionalExtras.filter(x => x.Selected === true).map(x => x.Id)];
      const bundle = new SetBasketModel(
        context.state.basket.BundleId,
        context.state.basket.PromoCode,
        context.state.basket.NewClubId,
        null,
        selectedAddOns,
        selectedExtras,
      );
      bundle.preserveCharity = true;

      await context.dispatch('setBasketBundle', bundle);
    },

    async setLateReferral(context, {
      referrerIdentifier, isVerified, referrerName, referrerGuid = null,
    }) {
      context.commit('setLateReferral', {
        referrerIdentifier, isVerified, referrerName, referrerGuid,
      });
    },

    async clearLateReferral(context) {
      context.commit('clearLateReferral');
    },

    async selectProfessional(context, professional) {
      context.commit('setSelectedProfessional', professional);

      const selectedAddOns = [...context.state.basket.AdditionalItems.filter(x => x.Selected === true).map(x => x.Id)];
      if (context.state.selectedLessonBundle) {
        selectedAddOns.push(context.state.selectedLessonBundle.BundleId);
      }
      const selectedExtras = [...context.state.basket.AdditionalExtras.filter(x => x.Selected === true).map(x => x.Id)];
      const bundle = new SetBasketModel(
        context.state.basket.BundleId,
        context.state.basket.PromoCode,
        context.state.basket.NewClubId,
        null,
        selectedAddOns,
        selectedExtras,
      );
      bundle.preserveCharity = true;

      await context.dispatch('setBasketBundle', bundle);
    },

    async clearProfessional(context) {
      context.commit('setSelectedProfessional', null);

      const selectedAddOns = [...context.state.basket.AdditionalItems.filter(x => x.Selected === true).map(x => x.Id)];
      if (context.state.selectedLessonBundle) {
        selectedAddOns.push(context.state.selectedLessonBundle.BundleId);
      }
      const selectedExtras = [...context.state.basket.AdditionalExtras.filter(x => x.Selected === true).map(x => x.Id)];
      const bundle = new SetBasketModel(
        context.state.basket.BundleId,
        context.state.basket.PromoCode,
        context.state.basket.NewClubId,
        null,
        selectedAddOns,
        selectedExtras,
      );
      bundle.preserveCharity = true;

      await context.dispatch('setBasketBundle', bundle);
    },

    refreshBasket(context) {
      return new Promise((resolve) => {
        context.commit('setRefreshingBasket', true, { root: true });
        services.checkout.getBasket(null, true).then((basket) => {
          context.commit('pushBasket', basket);
          context.commit('setRefreshingBasket', false, { root: true });
          resolve();
        });
      });
    },

    emptyBasket(context) {
      return new Promise((resolve) => {
        context.commit('setRefreshingBasket', true, { root: true });
        context.commit('clearLateReferral');
        context.commit('setSelectedProfessional', null);
        context.commit('setSelectedLessonBundle', null);
        services.checkout.emptyBasket().then(() => {
          services.checkout.getBasket(null, true).then((basket) => {
            context.commit('pushBasket', basket);
            context.commit('setRefreshingBasket', false, { root: true });
            resolve();
          });
        });
      });
    },

    async checkIfClubExtrasAvailable(context, clubId = 0) {
      if (clubId > 0) {
        const available = await services.member.clubHasPurchasableExtras(clubId);
        context.commit('setClubExtrasAvailable', available);
      }
    },

    async setStandaloneExtraInBasket(context, { extraId }) {
      return new Promise((resolve, reject) => {
        context.commit('setRefreshingBasket', true, { root: true });
        services.checkout.setStandaloneExtraBasket(extraId)
          .then(() => {
            services.checkout.getBasket(null, false).then((basket) => {
              context.commit('pushBasket', basket);
              context.commit('setRefreshingBasket', false, { root: true });
              resolve(basket);
            });
          })
          .catch((error) => {
            context.commit('setRefreshingBasket', false, { root: true });
            console.error('Failed to set standalone extra basket:', error);
            reject(error);
          });
      });
    },

    async startStandaloneExtraPayment(context, { extraId, cardId = null }) {
      return services.checkout.startStandaloneExtraPayment(extraId, cardId);
    },
  },
};

export { basketStore };
